import { type Edge, type Node } from '@xyflow/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { BpmnDiagramCallbacks } from './bpmn-diagram.types'
import { resolveSequenceSource } from './bpmn-graph.mapper'
import { NEW_NODE_PANEL_TYPE, NewNodePanelData } from './components/NewNodePanel'
import { snapPointToGrid } from './grid-snapping.util'
import { getNodeDimensions } from './node-visuals'
import { planLaneNodeDrop } from './palette-drop.util'

import { CanvasData } from '../../bpmn-canvas.types'
import { GRID_SNAP_SIZE, GraphColors } from '../../bpmn-graph.constants'
import { Point } from '../../dictionary/geometry.types'
import { NodeTypeGrouped } from '../../dictionary/nodes-types'

/** RF id of the transient New Node panel node and its dashed source edge. */
const NEW_NODE_PANEL_ID = 'new-node-panel'

/** Where the panel sits and which node the sequence will start from. */
export interface NewNodePanelPlacement {
  position: Point
  sourceRfId: string
}

export interface NewNodePanelApi {
  /** The panel node plus the edge from the connection source, or null when closed */
  graph: { node: Node<NewNodePanelData>; edge: Edge } | null
  open: (placement: NewNodePanelPlacement) => void
  /** Pane click dismissal, swallowing the click that opened the panel */
  dismissOnPaneClick: () => void
}

/**
 * New Node panel (workflow editor's SelectionNode twin): opened where a
 * connection drag was dropped on empty lane space; picking a type creates the
 * node there plus the sequence from the drag's source.
 */
export function useNewNodePanel({
  data,
  readonly,
  graphNodes,
  onDropError,
  onNodeCreateWithSequence,
}: {
  data: CanvasData
  readonly: boolean
  /** Canonical (mapper-produced) nodes — a vanished source dismisses the panel */
  graphNodes: { id: string }[]
  onDropError: BpmnDiagramCallbacks['onDropError']
  onNodeCreateWithSequence: BpmnDiagramCallbacks['onNodeCreateWithSequence']
}): NewNodePanelApi {
  const [panel, setPanel] = useState<NewNodePanelPlacement | null>(null)
  // The mouseup that drops the connection can surface as a pane click right
  // after onConnectEnd — swallow that one so it doesn't dismiss the panel it
  // just opened (the workflow editor's firstPaneClickAfterConnect).
  const skipDismissOnPaneClick = useRef(false)

  const onSelect = useCallback(
    (option: NodeTypeGrouped) => {
      if (!panel) {
        return
      }
      setPanel(null)
      const position = snapPointToGrid(panel.position, GRID_SNAP_SIZE)
      const source = resolveSequenceSource(panel.sourceRfId, data)
      if (!source || !option.type) {
        return
      }
      const plan = planLaneNodeDrop(position, option.type, data, getNodeDimensions)
      if (!plan.swimlane) {
        return
      }
      if (!plan.valid) {
        onDropError(plan.error)

        return
      }
      onNodeCreateWithSequence({
        swimlane: plan.swimlane,
        position: plan.position,
        droppedEntity: { type: option.type },
        source,
      })
    },
    [panel, data, onDropError, onNodeCreateWithSequence]
  )

  const graph = useMemo(
    () =>
      panel
        ? {
            node: {
              id: NEW_NODE_PANEL_ID,
              type: NEW_NODE_PANEL_TYPE,
              position: panel.position,
              data: { onSelect } satisfies NewNodePanelData,
              draggable: false,
              selectable: false,
              zIndex: 10000,
            },
            edge: {
              id: NEW_NODE_PANEL_ID,
              source: panel.sourceRfId,
              target: NEW_NODE_PANEL_ID,
              // Workflow editor's selection-node edge look: solid blue orthogonal line
              type: 'smoothstep',
              style: { stroke: GraphColors.blue_500, strokeWidth: 2 },
              selectable: false,
              focusable: false,
              deletable: false,
            },
          }
        : null,
    [panel, onSelect]
  )

  // Dismissals beyond pane click / picking a type: Escape, the canvas turning
  // readonly, or the source node vanishing (a collaborator's delete) — the edge
  // would dangle from a node React Flow no longer knows.
  useEffect(() => {
    if (!panel) {
      return
    }
    if (readonly || !graphNodes.some((node) => node.id === panel.sourceRfId)) {
      setPanel(null)

      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPanel(null)
      }
    }
    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [panel, readonly, graphNodes])

  const open = useCallback((placement: NewNodePanelPlacement) => {
    skipDismissOnPaneClick.current = true
    setPanel(placement)
  }, [])

  const dismissOnPaneClick = useCallback(() => {
    if (skipDismissOnPaneClick.current) {
      skipDismissOnPaneClick.current = false

      return
    }
    setPanel(null)
  }, [])

  return { graph, open, dismissOnPaneClick }
}
